import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import {
    GetMentorsProps,
    getPeopleSearch,
    getTeam,
    getTeamDetails,
    GetTeamResponse,
    GetUnAssignedResponse,
    SaveExamsProps,
} from '../../../apis/api.egg'
import { ROLES } from '../../../constants'
import { useUser } from '../../../context/UserContext'
import { MenuProps } from '../../../layouts/NavBar/useNavBarItems'
import { MeetLauncher } from '../Components/MeetLauncher'
import { ModalMentors } from '../Components/ModalMentors'
import { OptionsSearchBox } from '../Components/SearchBox'
import { Team } from '../Team'
import { ReportsContextProvider } from './ReportsContext'
import { useCourses } from '../../../hooks/ApiHooks/useCourses'

const Context = createContext({} as TeamContext)
Context.displayName = 'TeamContext'

export const useTeam = () => useContext<TeamContext>(Context)

export const TeamContextProvider = ({
    networkId,
    courseID,
    children = <Team />,
}: {
    networkId?: string
    courseID?: string
    children?: ReactNode
}) => {
    const { t } = useTranslation('teamManager')
    const { role } = useUser()
    const { getNetworkData } = useCourses()
    const networkName = getNetworkData(networkId)?.networkName ?? null
    const [isSearching, setIsSearching] = useState<boolean>(false)
    const mentorsSearch = useState<GetMentorsProps | null>(null)
    const studentItemMoreOptionState = useState<string | null>(null)
    const actionsView = useState<ActionsView>('teams')
    const meeting = useState<string | null>(null)
    const examData = useState(initialExamData)
    const [pagination, setPagination] = useState({
        teams: 1,
        unassigned: 1,
    })

    /** Get Team list */
    const { data: team } = useQuery(
        [courseID, 'Team', 'Teams list', pagination.teams],
        () => getTeam(courseID, pagination.teams),
        {
            enabled: !!courseID,
            keepPreviousData: true,
        }
    )

    // Detect if we're using hierarchical routes or legacy routes
    const isHierarchicalRoute = window.location.pathname.includes('/network/')

    // Build URLs based on route type
    const getUrl = (section: string) => {
        if (isHierarchicalRoute && networkId) {
            return `/network/${networkId}/cohorts/${courseID}/${section}`
        }
        // Legacy routes
        return `/admin/${section}/${courseID}`
    }

    const mainLinks: MenuProps[] = [
        {
            name: t('main-links.cohorts'),
            url: `/network/${networkId}/cohorts`,
            active: window.location.pathname.valueOf() === `/network/${networkId}/cohorts`,
            icon: ['far', 'list'],
            activeIcon: ['far', 'list'],
        },
        {
            name: t('main-links.room'),
            url: getUrl('overview'),
            active: window.location.pathname.includes('/overview'),
            icon: ['far', 'monitor-waveform'],
            activeIcon: ['far', 'monitor-waveform'],
        },
        {
            name: t('main-links.students'),
            url: getUrl('team'),
            active: window.location.pathname.includes('/team'),
            icon: ['far', 'people-group'],
            activeIcon: ['far', 'people-group'],
        },
    ]

    // Add reports menu - PROFESSOR can now view but not export
    role !== ROLES.PROFESSOR &&
        mainLinks.push({
            name: t('reports-section.title'),
            url: getUrl('reports'),
            active:
                window.location.pathname.includes('/reports') &&
                !window.location.pathname.includes(`/network/${networkId}/reports`),
            icon: ['far', 'chart-mixed'],
            activeIcon: ['far', 'chart-mixed'],
        })

    // Add Setting menu - only for non-PROFESSOR roles
    role !== ROLES.PROFESSOR &&
        mainLinks.push({
            name: t('main-links.settings'),
            url: getUrl('settings'),
            active: window.location.pathname.includes('/settings'),
            icon: ['far', 'gear'],
            activeIcon: ['far', 'gear'],
        })

    /** Build SearchBox available search options */
    const searchOptions: OptionsSearchBox[] = useMemo(
        () => [
            {
                type: 'people',
                label: t('search.badge-people'),
                placeholder: t('search.option-placeholder-people'),
                icon: ['far', 'user'],
            },
            {
                type: 'teams',
                label: `${t('search.badge-team')} Nº`,
                placeholder: t('search.option-placeholder-teams'),
                icon: ['far', 'users'],
            },
        ],
        [t]
    )

    /** Populate search assistant list, with student names & team numbers */
    useEffect(() => {
        if (!team) return

        const indexPeople = searchOptions.findIndex((item) => item.type === 'people')
        if (indexPeople < 0) return

        const indexTeams = searchOptions.findIndex((item) => item.type === 'teams')
        if (indexTeams < 0) return
        searchOptions[indexTeams]['list'] = [...team.stats.allTeamNumbers]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [team])
    return (
        <Context.Provider
            value={{
                team,
                courseID: team?.course._id,
                algorithmID: team?.algorithm?._id,
                networkId,
                networkName,
                mainLinks,
                actionsView,
                searchOptions,
                isSearching,
                setIsSearching,
                mentorsSearch,
                studentItemMoreOptionState,
                examData,
                meeting,
                teamPage: pagination.teams,
                unassignedPage: pagination.unassigned,
                setPagination,
            }}
        >
            <ReportsContextProvider>{children}</ReportsContextProvider>
            <MeetLauncher />
            <ModalMentors />
        </Context.Provider>
    )
}

export const initialExamData: ExamValues = {
    title: '',
    date: new Date(),
    notes: [],
}

// Interface
interface TeamContext {
    team?: GetTeamResponse['data']
    courseID?: string
    algorithmID?: string
    networkId: string | null
    networkName: string | null
    unassigned?: GetUnAssignedResponse['data']
    mainLinks: MenuProps[]
    actionsView: [ActionsView, Dispatch<SetStateAction<ActionsView>>]
    searchOptions: OptionsSearchBox[]
    isSearching: boolean
    setIsSearching: Dispatch<SetStateAction<boolean>>
    mentorsSearch: [GetMentorsProps | null, Dispatch<SetStateAction<GetMentorsProps | null>>]
    studentItemMoreOptionState: [string | null, Dispatch<SetStateAction<string | null>>]
    examData: [ExamValues, Dispatch<SetStateAction<ExamValues>>]
    meeting: [string | null, Dispatch<SetStateAction<string | null>>]
    teamPage: number
    unassignedPage: number
    setPagination: Dispatch<
        SetStateAction<{
            teams: number
            unassigned: number
        }>
    >
}

export type ActionsView = 'teams' | 'people'
export type SearchResults = null | ReturnType<typeof getTeamDetails> | ReturnType<typeof getPeopleSearch>

interface ExamValues {
    title: string
    date: Date
    notes: SaveExamsProps['exam']['notes']
}
